import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import useSeo from "../lib/useSeo";

export default function LibraryPage() {
    useSeo({
        title: "The Library",
        description:
            "Browse every animated comic and novella in the EIGOO Books catalog — from the flagship Sovereign Commander to the Obsidian Archive anthology.",
    });
    const [comics, setComics] = useState([]);

    useEffect(() => {
        api.get("/comics").then(({ data }) => setComics(data));
    }, []);

    return (
        <main data-testid="library-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
            <div className="mb-16">
                <div className="eyebrow mb-3">The Library</div>
                <h1 className="font-serif text-5xl md:text-7xl tracking-tight">
                    Every title in <span className="text-gold-gradient italic">circulation</span>
                </h1>
                <p className="text-[#A1A1AA] mt-6 max-w-2xl">
                    A curated catalog of EIGOO Books — animated comic series, novellas, and
                    archive volumes. Each chapter pressed by hand, animated for the eye.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {comics.map((c, i) => (
                    <motion.div
                        key={c.id}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.6, delay: i * 0.08 }}
                    >
                        <Link
                            to={`/comic/${c.slug}`}
                            data-testid={`library-card-${c.slug}`}
                            className="block frame-gold p-6 group"
                        >
                            <div
                                className="aspect-[3/4] bg-cover bg-center mb-6 relative overflow-hidden"
                                style={{ backgroundImage: `url(${c.cover_url})` }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                                {c.is_flagship && (
                                    <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-[#0a0a0a]/80 px-2.5 py-1 border border-[#D4AF37]/40">
                                        <Crown className="w-3 h-3 text-[#D4AF37]" />
                                        <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]">
                                            Flagship
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="eyebrow text-[10px] mb-2">
                                {c.chapter_count} chapter{c.chapter_count === 1 ? "" : "s"}
                            </div>
                            <h3 className="font-serif text-2xl text-white group-hover:text-[#E6D59A] transition-colors">
                                {c.title}
                            </h3>
                            <p className="italic text-[#A1A1AA] text-sm mt-2">{c.tagline}</p>
                            <p className="text-[#cfcfcf]/70 text-sm mt-4 line-clamp-3">
                                {c.synopsis}
                            </p>
                            <div className="mt-6 flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                                Open volume <ArrowRight className="w-3 h-3" />
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </div>
        </main>
    );
}
